/**
 * Display formatting.
 *
 * Every timestamp from the API is epoch **milliseconds**.
 */
import {
  format,
  formatDistanceToNowStrict,
  formatDuration,
  intervalToDuration,
  isPast,
} from "date-fns";

export function formatTimestamp(ms: number): string {
  return format(new Date(ms), "yyyy-MM-dd HH:mm");
}

export function formatRelative(ms: number): string {
  return `${formatDistanceToNowStrict(new Date(ms))} ago`;
}

/** Deadlines are shown as "in 3 days" / "2 hours ago". */
export function formatDeadline(ms: number | null): string {
  if (ms === null) return "No deadline";
  const date = new Date(ms);
  const distance = formatDistanceToNowStrict(date);
  return isPast(date) ? `Closed ${distance} ago` : `Closes in ${distance}`;
}

/** "in 12 minutes" / "3 minutes ago", for a moment that may be either side of now. */
export function formatCountdown(ms: number): string {
  const date = new Date(ms);
  const distance = formatDistanceToNowStrict(date);
  return isPast(date) ? `${distance} ago` : `in ${distance}`;
}

const DURATION_UNITS = ["years", "months", "days", "hours", "minutes", "seconds"] as const;

/**
 * "3 days 4 hours", "12 minutes 30 seconds": the time from `now` to `until` in its
 * two largest non-zero units, for a countdown that visibly moves. "0 seconds" once
 * it has passed.
 */
export function formatTimeLeft(until: number, now: number): string {
  const duration = intervalToDuration({ start: now, end: Math.max(now, until) });
  const first = DURATION_UNITS.findIndex((unit) => (duration[unit] ?? 0) > 0);
  if (first < 0) return "0 seconds";
  return formatDuration(duration, { format: DURATION_UNITS.slice(first, first + 2), zero: false });
}

export function isDeadlinePassed(ms: number | null): boolean {
  return ms !== null && isPast(new Date(ms));
}

/**
 * An em-dash for a metric that does not exist.
 *
 * The formatters below accept null because a failed submission genuinely has no
 * score — the server's CHECK constraint guarantees it. Defaulting to 0 instead
 * would render "0.0000 m", which reads as a perfect run.
 */
const ABSENT = "—";

/** Metres, to millimetre precision — errors of interest are around 0.1 m. */
export function formatError(metres: number | null): string {
  return metres === null ? ABSENT : `${metres.toFixed(4)} m`;
}

/** Degrees, to a tenth -- heading bands are tens of degrees. */
export function formatDegrees(deg: number | null): string {
  return deg === null ? ABSENT : `${deg.toFixed(1)}°`;
}

export function formatSeconds(s: number | null): string {
  if (s === null) return ABSENT;
  if (s < 60) return `${s.toFixed(1)}s`;
  const minutes = Math.floor(s / 60);
  return `${minutes}m ${Math.round(s % 60)}s`;
}

export function formatSteps(n: number | null): string {
  return n === null ? ABSENT : n.toLocaleString();
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** A short, stable label for a job id in dense tables. */
export function shortId(id: string): string {
  return id.slice(0, 8);
}

const TIER_LABEL: Record<string, string> = {
  perfect: "Perfect",
  partial: "Partial",
  failed: "Missed",
};

export function goalLabel(tier: string): string {
  return TIER_LABEL[tier] ?? tier;
}

/** Whether the challenge still withholds its data and runs from students at `now`. */
export function notOpenYet(challenge: { opens_at: number | null }, now: number = Date.now()): boolean {
  return challenge.opens_at !== null && challenge.opens_at !== undefined && now < challenge.opens_at;
}

/** "2d 03:04:05", or "03:04:05" under a day: a clock that visibly ticks. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(total / 86400);
  const pad = (n: number) => String(n).padStart(2, "0");
  const hms = `${pad(Math.floor((total % 86400) / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
  return days > 0 ? `${days}d ${hms}` : hms;
}

/** Course announcements use New York time, including daylight saving changes. */
export function formatEasternTimestamp(ms: number): string {
  return `${new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(ms)} ET`;
}
